"use client";

// Horloge de la démonstration. Tout ce qui bouge dans une scène est une
// fonction du temps écoulé dans cette scène, en millisecondes : mettre en
// pause revient à arrêter l'horloge, changer de chapitre à la remettre à
// zéro, et le mode « mouvement réduit » à la poser sur l'instant le plus
// parlant de la scène sans jamais la faire avancer.
//
// Chaque scène reçoit SA propre valeur de temps. Quand un chapitre laisse
// la place au suivant, la scène sortante garde son horloge figée sur sa
// dernière image pendant son fondu, au lieu de revenir à son état initial.
//
// Les hooks ne déclenchent un rendu React qu'au franchissement d'un seuil
// (un clic, une lettre tapée, un chiffre qui change) : les barres de
// progression et le curseur lisent le temps directement, sans rendu.

import {
    createContext,
    useContext,
    useLayoutEffect,
    useEffect,
    useRef,
    useState,
} from "react";
import { useMotionValueEvent, useTransform } from "motion/react";

const SceneTimeContext = createContext(null);

export const SceneTimeProvider = SceneTimeContext.Provider;

/** Temps écoulé dans la scène courante (MotionValue, en ms). */
export function useSceneTime() {
    return useContext(SceneTimeContext);
}

export const useIsomorphicLayoutEffect =
    typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * Nombre de seuils déjà franchis. `useStep([1000, 2500])` vaut 0 avant
 * 1 s, 1 entre 1 s et 2,5 s, puis 2.
 */
export function useStep(thresholds) {
    const time = useSceneTime();
    const count = (value) => {
        let passed = 0;
        for (const threshold of thresholds) {
            if (value >= threshold) passed += 1;
        }
        return passed;
    };
    const [step, setStep] = useState(() => count(time.get()));
    const stepRef = useRef(step);

    useMotionValueEvent(time, "change", (value) => {
        const next = count(value);
        if (next !== stepRef.current) {
            stepRef.current = next;
            setStep(next);
        }
    });

    return step;
}

/** Vrai dès que la scène a dépassé `ms`. */
export function useAt(ms) {
    return useStep([ms]) === 1;
}

/** Progression de 0 à 1 entre deux instants, sans rendu React. */
export function useSpan(start, end) {
    const time = useSceneTime();
    return useTransform(time, [start, end], [0, 1], { clamp: true });
}

/**
 * Texte tapé au clavier : `text` s'écrit à partir de `start`, une lettre
 * toutes les `msPerChar` millisecondes.
 */
export function useTyped(text, start, msPerChar = 45) {
    const time = useSceneTime();
    const length = (value) =>
        Math.max(0, Math.min(text.length, Math.floor((value - start) / msPerChar)));
    const [typed, setTyped] = useState(() => length(time.get()));
    const typedRef = useRef(typed);

    useMotionValueEvent(time, "change", (value) => {
        const next = length(value);
        if (next !== typedRef.current) {
            typedRef.current = next;
            setTyped(next);
        }
    });

    return text.slice(0, typed);
}

const easeOutCubic = (x) => 1 - Math.pow(1 - x, 3);

/**
 * Nombre qui monte de `from` à `to` entre deux instants, arrondi à
 * `step` près (un montant en euros ne défile pas au centime).
 */
export function useCount(start, end, from, to, step = 1) {
    const time = useSceneTime();
    const value = (t) => {
        const progress = Math.max(0, Math.min(1, (t - start) / (end - start)));
        return Math.round((from + (to - from) * easeOutCubic(progress)) / step) * step;
    };
    const [count, setCount] = useState(() => value(time.get()));
    const countRef = useRef(count);

    useMotionValueEvent(time, "change", (t) => {
        const next = value(t);
        if (next !== countRef.current) {
            countRef.current = next;
            setCount(next);
        }
    });

    return count;
}
